/**
 * The grouped in-app notification centre.
 *
 * Takes the page's fetched rows and renders them as module sections rather
 * than one flat list. Everything the flat list did still works — the page owns
 * fetching, filters, selection and the mutations, and passes them in.
 *
 * Three things happen here that the flat list could not do:
 *  - rows are grouped by module and coalesced into threads
 *    ({@link groupNotifications});
 *  - a module the user has no access to is not rendered at all
 *    ({@link useNotificationModuleAccess});
 *  - `background`-tier rows are held back in a quieter Activity feed instead of
 *    competing with real work.
 */
import { useMemo, useState } from 'react';

import type { Notification } from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { AlertCircle, Bell, Loader2, Moon, RefreshCw } from 'lucide-react';
import { groupNotifications, type NotificationModuleGroup } from './groupNotifications';
import NotificationGroupSection from './NotificationGroupSection';
import { resolveSeverity } from './severity';
import { useNotificationModuleAccess } from './useNotificationModuleAccess';

export interface NotificationCentreProps {
  notifications: Notification[];
  loading: boolean;
  /** Message from the last failed fetch, or null. */
  error: string | null;
  onRetry: () => void;
  selectedIds: Set<number>;
  onSelectionChange: (ids: number[], selected: boolean) => void;
  onOpen: (notification: Notification) => void;
  onMarkRead: (ids: number[]) => void;
  onDelete: (id: number) => void;
  /** Include `background`-tier rows in the sections. */
  showBackground: boolean;
  /** Flip the Activity toggle from the inline hint. */
  onToggleBackground: () => void;
}

/** A section defaults to open when it has unread rows or is short. */
function defaultOpen(group: NotificationModuleGroup): boolean {
  return group.unreadCount > 0 || group.totalCount <= 5;
}

export default function NotificationCentre({
  notifications,
  loading,
  error,
  onRetry,
  selectedIds,
  onSelectionChange,
  onOpen,
  onMarkRead,
  onDelete,
  showBackground,
  onToggleBackground,
}: NotificationCentreProps) {
  const { canSeeDomain, isLoading: accessLoading } = useNotificationModuleAccess();
  const [openState, setOpenState] = useState<Record<string, boolean>>({});

  const { foreground, backgroundTotal } = useMemo(() => {
    const kept: Notification[] = [];
    let background = 0;
    notifications.forEach((notification) => {
      const isBackgroundRow = resolveSeverity(notification) === 'background';
      if (isBackgroundRow) background += 1;
      if (isBackgroundRow && !showBackground) return;
      kept.push(notification);
    });
    return { foreground: kept, backgroundTotal: background };
  }, [notifications, showBackground]);

  const allGroups = useMemo(() => groupNotifications(foreground), [foreground]);

  const { groups, hiddenCount } = useMemo(() => {
    const visible: NotificationModuleGroup[] = [];
    let suppressed = 0;
    allGroups.forEach((group) => {
      if (canSeeDomain(group.domain)) {
        visible.push(group);
      } else {
        suppressed += group.totalCount;
      }
    });
    return { groups: visible, hiddenCount: suppressed };
  }, [allGroups, canSeeDomain]);

  // Waiting on permissions too, not just the rows: a section must never flash
  // into view for a module the user turns out not to have access to.
  if (loading || accessLoading) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <Loader2 className="h-6 w-6 mx-auto mb-3 animate-spin" />
          Loading notifications...
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <AlertCircle className="h-10 w-10 mx-auto mb-3 text-red-500" />
          <p className="font-medium">{error}</p>
          <p className="text-sm text-muted-foreground mt-1">
            Your notifications could not be loaded.
          </p>
          <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (groups.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No notifications found</p>
          {hiddenCount > 0 && (
            <p className="text-xs mt-2">
              {hiddenCount} notification{hiddenCount === 1 ? '' : 's'} hidden — they belong to
              modules you do not have access to.
            </p>
          )}
          {backgroundTotal > 0 && !showBackground && (
            <Button variant="ghost" size="sm" className="mt-3" onClick={onToggleBackground}>
              <Moon className="h-4 w-4 mr-2" />
              Show {backgroundTotal} background update{backgroundTotal === 1 ? '' : 's'}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <NotificationGroupSection
          key={group.domain}
          group={group}
          open={openState[group.domain] ?? defaultOpen(group)}
          onOpenChange={(next) =>
            setOpenState((prev) => ({ ...prev, [group.domain]: next }))
          }
          onMarkGroupRead={(target) => onMarkRead(target.unreadIds)}
          selectedIds={selectedIds}
          onSelectionChange={onSelectionChange}
          onOpen={onOpen}
          onMarkRead={onMarkRead}
          onDelete={onDelete}
        />
      ))}

      {(backgroundTotal > 0 || hiddenCount > 0) && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-muted-foreground">
          <span>
            {hiddenCount > 0
              ? `${hiddenCount} notification${hiddenCount === 1 ? '' : 's'} hidden — they belong to modules you do not have access to.`
              : ''}
          </span>
          {backgroundTotal > 0 && (
            <Button variant="ghost" size="sm" onClick={onToggleBackground}>
              <Moon className="h-4 w-4 mr-2" />
              {showBackground
                ? 'Hide background updates'
                : `Show ${backgroundTotal} background update${backgroundTotal === 1 ? '' : 's'}`}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
